export async function renderMapChart(container, config) {
  const d3 = window.d3;
  const topojson = window.topojson;

  const margin = {
    top: config.title ? config.margin.top * 0.25 : config.margin.top * 0.1,
    right: config.margin.right * 0.1,
    bottom: config.margin.bottom * 0.1,
    left: config.margin.left * 0.1,
  };
  if (config.subtitle) margin.top += 10;
  const width = config.width;
  const height = config.height;
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  if (!config.mapURL || !config.data) {
    console.warn('Map or data not provided.');
    return;
  }

  // Clear previous chart
  const containerSelection = d3.select(container);
  containerSelection.selectAll('*').remove();

  const svg = containerSelection
    .append('svg')
    .attr('viewBox', [0, 0, width, height])
    .append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`);

  // Load map
  const topoData = await d3.json(config.mapURL);
  const mapObjectName = Object.keys(topoData.objects)[0];
  const geojson = topojson.feature(topoData, topoData.objects[mapObjectName]);

  const projection = d3
    .geoMercator()
    .fitSize([innerWidth, innerHeight], geojson);
  const path = d3.geoPath(projection);

  // Use pre-parsed data
  const data = config.data;
  const dataIndex = config.dataIndex || 0;
  const geoJoinKey = 'Communes'; // must match your GeoJSON property name

  // Build a lookup Map: label → value
  const dataMap = new Map();
  data.series.forEach((series) => {
    const value = series.values[dataIndex];
    if (!isNaN(value)) dataMap.set(series.name.trim(), value);
  });

  const dataValues = Array.from(dataMap.values());
  const domainExtent = dataValues.length > 0 ? d3.extent(dataValues) : [0, 1];

  // Color configuration
  let colorScale;
  if (config.mapColorType === 'scheme') {
    // Use discrete color scheme (e.g. d3.schemeBlues)
    const scheme = d3[`scheme${config.mapColor}`];
    const numColors = config.mapGroups || 9;
    colorScale = d3
      .scaleQuantize()
      .domain(domainExtent)
      .range(scheme[numColors] || scheme[scheme.length - 1]);
  } else {
    // Use continuous interpolator (e.g. d3.interpolateBlues)
    const interpolator = d3[`interpolate${config.mapColor}`];
    colorScale = d3
      .scaleSequential()
      .domain(domainExtent)
      .interpolator(interpolator);
  }
  // Reverse colors
  if (config.mapColorReverse) {
    if (config.mapColorType === 'scheme') {
      colorScale.range(colorScale.range().slice().reverse());
    } else {
      colorScale.domain(colorScale.domain().slice().reverse());
    }
  }

  // Tooltip
  const tooltip = d3
    .select('body')
    .append('div')
    .style('position', 'absolute')
    .style('background', 'white')
    .style('border', '1px solid #ccc')
    .style('padding', '4px 8px')
    .style('border-radius', '4px')
    .style('pointer-events', 'none')
    .style('font-size', '12px')
    .style('display', 'none')
    .style('z-index', '9999');

  // Draw map
  const mapGroup = svg
    .append('g')
    .selectAll('path')
    .data(geojson.features)
    .join('path')
    .attr('d', path)
    .attr('fill', (d) => {
      const key = d.properties[geoJoinKey]?.trim() || '';
      const val = dataMap.get(key);
      return val !== undefined ? colorScale(val) : '#ccc';
    })
    .attr('stroke', '#fff')
    .attr('pointer-events', 'visiblePainted');

  if (config.tooltip) {
    mapGroup
      .data(geojson.features)
      .attr('stroke', '#fff')
      .on('mouseover', function (event, d) {
        const key = d.properties[geoJoinKey]?.trim() || '';
        const val = dataMap.get(key);
        tooltip
          .style('display', 'block')
          .html(`${key}<br>${val !== undefined ? val + '%' : 'Geen data'}`)
          .style('left', event.pageX + 10 + 'px')
          .style('top', event.pageY - 20 + 'px');
      })
      .on('mousemove', (event) => {
        tooltip
          .style('left', event.pageX + 10 + 'px')
          .style('top', event.pageY - 20 + 'px');
      })
      .on('mouseout', () => tooltip.style('display', 'none'));
  }

  if (config.title) {
    svg
      .append('text')
      .attr('x', innerWidth / 2)
      .attr('y', -margin.top / 2)
      .attr('text-anchor', 'middle')
      .style('font-size', '16px')
      .text(config.title);
  }

  if (config.subtitle) {
    svg
      .append('text')
      .attr('x', innerWidth / 2)
      .attr('y', -margin.top / 2 + 15)
      .attr('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('fill', '#666')
      .text(config.subtitle);
  }

  // Legend
  if (config.legend) {
    if (config.mapColorType === 'scheme') {
      // legend for scheme
      const legendGroup = svg
        .append('g')
        .attr('class', 'legend')
        .attr(
          'transform',
          `translate(${width - (config.mapGroups || 9) * 22}, 0)`
        ); // top-right corner

      // Safely get thresholds (only works on quantize scales)
      const thresholds = colorScale.thresholds ? colorScale.thresholds() : [];
      thresholds.unshift(domainExtent[0]);

      thresholds.forEach((t, i) => {
        const x = i * 20; // simple spacing, no extra offset
        const y = 10;

        // color boxes
        legendGroup
          .append('rect')
          .attr('x', x)
          .attr('y', y)
          .attr('width', 20)
          .attr('height', 10)
          .attr('fill', colorScale(t))
          .attr('stroke', 'none');

        if (i !== 0) {
          // vertical tick line
          legendGroup
            .append('line')
            .attr('x1', x)
            .attr('x2', x)
            .attr('y1', y)
            .attr('y2', y + 15)
            .attr('stroke', 'black');

          // text labels under lines
          legendGroup
            .append('text')
            .attr('x', x)
            .attr('y', y + 25)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .attr('font-size', 10)
            .attr('font-weight', '600')
            .attr('fill', 'black')
            .text(i === 1 ? Math.round(t) + '%' : Math.round(t));
        }
      });
    } else {
      // legend for interpolate
      const legendWidth = 100;
      const legendHeight = 10;

      const legend = svg
        .append('g')
        .attr('transform', `translate(${innerWidth - legendWidth - 20}, 20)`);

      // Create gradient
      const defs = svg.append('defs');
      const gradient = defs
        .append('linearGradient')
        .attr('id', 'legend-gradient');

      gradient
        .selectAll('stop')
        .data(d3.ticks(0, 1, 10))
        .enter()
        .append('stop')
        .attr('offset', (d) => `${d * 100}%`)
        .attr('stop-color', (d) =>
          colorScale(domainExtent[0] + d * (domainExtent[1] - domainExtent[0]))
        );

      // Draw color bar
      legend
        .append('rect')
        .attr('width', legendWidth)
        .attr('height', legendHeight)
        .style('fill', 'url(#legend-gradient)')
        .style('stroke', '#000')
        .style('stroke-width', 0.5);

      // Add min and max labels
      legend
        .append('text')
        .attr('x', 0)
        .attr('y', legendHeight + 12)
        .attr('font-size', 10)
        .attr('text-anchor', 'start')
        .text(Math.round(domainExtent[0]));

      legend
        .append('text')
        .attr('x', legendWidth)
        .attr('y', legendHeight + 12)
        .attr('font-size', 10)
        .attr('text-anchor', 'end')
        .text(Math.round(domainExtent[1]));
    }
  }
}
